import { useMemo } from 'react';
import type { TaxScenario } from '@/tax/types';
import { rothYearByYearSeries } from '@/tax/rothGrowth';
import { formatChartCurrency, formatCurrency, formatPercent } from '@/tax/format';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';

const PROJECTION_YEARS = 10;

const COLOR_ROTH = 'hsl(var(--chart-federal))';
const COLOR_IRA_CURRENT = 'hsl(var(--chart-state))';
const COLOR_IRA_HIGHER = 'hsl(var(--chart-niit))';

interface RothProjectionChartProps {
  scenario: TaxScenario;
  /** Baseline ETR — the rate they'd pay in retirement absent the conversion. */
  baselineEffectiveTaxRate: number;
  /** Future tax rate increase in percentage points (e.g. 0.05 = +5pp). */
  taxRateDelta: number;
}

export function RothProjectionChart({ scenario, baselineEffectiveTaxRate, taxRateDelta }: RothProjectionChartProps) {
  const principal = scenario.input.rothConversionAmount ?? 0;

  const data = useMemo(() => {
    const pretax = rothYearByYearSeries(
      principal,
      scenario.input.rothGrowthModel,
      scenario.input.rothGrowthRate,
      PROJECTION_YEARS,
    );
    const etrCurrent = Math.max(0, Math.min(1, baselineEffectiveTaxRate));
    const etrHigher = Math.max(0, Math.min(1, baselineEffectiveTaxRate + taxRateDelta));
    return pretax.map((v, t) => ({
      year: t,
      roth: v,
      iraCurrent: v * (1 - etrCurrent),
      iraHigher: v * (1 - etrHigher),
    }));
  }, [principal, scenario.input.rothGrowthModel, scenario.input.rothGrowthRate, baselineEffectiveTaxRate, taxRateDelta]);

  if (principal <= 0) {
    return (
      <div className="flex items-center justify-center h-[380px] text-sm text-muted-foreground">
        This scenario has no Roth conversion amount. Add a conversion to see the 10-year projection.
      </div>
    );
  }

  const final = data[data.length - 1];
  const gapAtCurrentRate = final.roth - final.iraCurrent;
  const gapAtHigherRate = final.roth - final.iraHigher;

  return (
    <div className="space-y-3">
      <ResponsiveContainer width="100%" height={380}>
        <LineChart data={data} margin={{ top: 20, right: 30, bottom: 5, left: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted-foreground))" opacity={0.2} />
          <XAxis
            dataKey="year"
            tick={{ fontSize: 14 }}
            axisLine={false}
            tickLine={false}
            label={{ value: 'Year', position: 'insideBottom', offset: -2, fontSize: 12 }}
          />
          <YAxis
            tick={{ fontSize: 14 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={formatChartCurrency}
            domain={[0, (dataMax: number) => dataMax * 1.1]}
          />
          <Tooltip
            formatter={(value: number) => formatCurrency(value)}
            labelFormatter={(year: number) => `Year ${year}`}
            contentStyle={{ fontSize: 13 }}
          />
          <Legend iconSize={12} wrapperStyle={{ fontSize: 14 }} />
          <Line
            type="monotone"
            dataKey="roth"
            name="Roth (tax-free)"
            stroke={COLOR_ROTH}
            strokeWidth={3}
            dot={false}
          />
          <Line
            type="monotone"
            dataKey="iraCurrent"
            name={`IRA after-tax @ ${formatPercent(baselineEffectiveTaxRate)}`}
            stroke={COLOR_IRA_CURRENT}
            strokeWidth={2}
            dot={false}
          />
          <Line
            type="monotone"
            dataKey="iraHigher"
            name={`IRA after-tax @ ${formatPercent(baselineEffectiveTaxRate + taxRateDelta)}`}
            stroke={COLOR_IRA_HIGHER}
            strokeWidth={2}
            strokeDasharray="5 4"
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <div className="rounded-md border p-3">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Year 10 Roth Value</p>
          <p className="font-mono font-bold">{formatCurrency(final.roth)}</p>
        </div>
        <div className="rounded-md border p-3">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
            Tax Avoided @ {formatPercent(baselineEffectiveTaxRate)}
          </p>
          <p className="font-mono font-bold text-emerald-600 dark:text-emerald-400">
            {formatCurrency(gapAtCurrentRate)}
          </p>
        </div>
        <div className="rounded-md border p-3">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
            Tax Avoided @ {formatPercent(baselineEffectiveTaxRate + taxRateDelta)}
          </p>
          <p className="font-mono font-bold text-emerald-600 dark:text-emerald-400">
            {formatCurrency(gapAtHigherRate)}
          </p>
        </div>
      </div>
    </div>
  );
}
